import React, { useState } from "react";
import "./FundingSource.scss";
import { Connect } from "connect-web-sdk";
import useVipApiFetcher, {
  handleError,
  handleResponse,
} from "Utils/Hooks/useVipApiFetcher";
import { useHistory } from "react-router-dom";
import useMode, { MODE } from "Utils/Hooks/useMode";
import { Routes, ZEROBALANCEREASON } from "Utils/Constants";
import useTransactionInitiated from "Utils/Hooks/useTransactionInitiated";
import { faro } from "@grafana/faro-react";
import { Box, Button } from "@pav/pav-products-design-system";
import Loading from "Components/Loading";

const Finicity = ({
  onDataChanged,
  connectUrl,
  enrollmentStep,
  buttonText = "Connect Your Bank Account",
}) => {
  const history = useHistory();
  const mode = useMode();
  const vipApi = useVipApiFetcher();
  const { transactionInitiated } = useTransactionInitiated();
  const [isLoading, setIsLoading] = useState(false);
  const isDepositMode = mode === MODE.deposit;
  const onSuccess = async () => {
    const response = await vipApi.fetch("/api/finicity/account");
    if (response.status === 200) {
      onDataChanged({
        fundingSourceType: "FIN",
      });
    } else {
      faro.api.pushError(new Error("Finicity Fund Type Update Failed"), {
        context: response,
      });
      console.log("Finicity Fund Type Update Failed");
    }
  };

  const finicityAddFundingSourceFailure = (err) => {
    const customErrorButtons =
      mode === MODE.manageBanks
        ? [
            {
              label: "Try Again",
              route: Routes.MY_ACCOUNTS,
            },
          ]
        : [
            {
              label: "Try Again",
              route: Routes.MY_ACCOUNTS,
            },
            {
              label: `Complete ${isDepositMode ? "Deposit" : "Withdraw"}`,
              route: isDepositMode ? Routes.DEPOSIT : Routes.WITHDRAW,
            },
          ];
    faro.api.pushError(new Error("Finicity Add Funding Source Error"), {
      context: err,
    });
    handleError(err, history, customErrorButtons);
  };

  const onFinicityAddFundingSource = async () => {
    setIsLoading(true);
    const customErrorButtons =
      mode === MODE.manageBanks
        ? [
            {
              label: "Try Again",
              route: Routes.MY_ACCOUNTS,
            },
          ]
        : [
            {
              label: "Try Again",
              route: Routes.MY_ACCOUNTS,
            },
            {
              label: `Complete ${isDepositMode ? "Deposit" : "Withdraw"}`,
              route: isDepositMode ? Routes.DEPOSIT : Routes.WITHDRAW,
            },
          ];

    vipApi
      .fetch("/api/bank/finicity", "POST")
      .then((response) => handleResponse(history, response))
      .then((response) => {
        if (response.status === 200) {
          history.push({
            pathname:
              mode === "managebanks"
                ? "/addAccountSuccess"
                : isDepositMode
                  ? "/addFunds"
                  : "/withdrawFunds",
          });
        } else {
          let isZeroBalance =
            response.body?.operatorErrorMessage.includes(ZEROBALANCEREASON);
          let message = isZeroBalance
            ? "The bank account you are trying to connect must have more than $0."
            : (response.body.patronErrorMessage ??
              "Error Adding Funding Source");
          let zeroBalanceErrorButtons = !transactionInitiated
            ? [
                {
                  label: "Manage Funding Sources",
                  route: Routes.MY_ACCOUNTS,
                  icon: true,
                },
                {
                  label: `Cancel`,
                  route: isDepositMode ? Routes.DEPOSIT : Routes.WITHDRAW,
                  icon: false,
                },
              ]
            : [
                {
                  label: "Manage Funding Sources",
                  route: Routes.MY_ACCOUNTS,
                  icon: true,
                },
              ];
          if (isZeroBalance) {
            faro.api.pushError(new Error("Finicity Zero Account Balance"), {
              context: response,
            });
            history.push({
              pathname: "/zerobalance",
              state: {
                isError: false,
                message: message,
                showButton: true,
                allowRetry: false,
                customButtons: zeroBalanceErrorButtons,
                transactionInitiated: false,
              },
            });
          } else {
            faro.api.pushError(new Error("Finicity Add Account Error"), {
              context: response,
            });
            history.push({
              pathname: "/error",
              state: {
                isError: true,
                message: message,
                showButton: true,
                allowRetry: false,
                customButtons: customErrorButtons,
                transactionInitiated: false,
              },
            });
          }
        }
      })
      .catch((error) => {
        faro.api.pushError(
          new Error("Finicity Add Funding Source Network Error"),
          { context: error },
        );
        handleError(error, history, customErrorButtons);
      });
  };

  const onSubmit = (event) => {
    event.preventDefault();
    faro.api.pushEvent("User clicked to connect a bank account using Finicity");
    const connectEventHandlers = {
      onDone: async (event) => {
        console.log("onDone", event);
        if (enrollmentStep) {
          onSuccess();
        } else {
          await onFinicityAddFundingSource();
        }
      },
      onCancel: (event) => {},
      onError: (event) => {
        console.log("onError", event);
        finicityAddFundingSourceFailure(event);
      },
      onUser: ({ action, code, customerId }) => {
        if (action === "End" && code === 200 && customerId) {
        }
      },
      onLoad: () => {
        console.log("loaded");
      },
    };
    const connectOptions = {
      overlay: "rgba(199,201,199, 0.5);z-index: 10000;",
    };
    Connect.launch(connectUrl, connectEventHandlers, connectOptions);
  };

  let button;
  if (isLoading) {
    button = <Loading />;
  } else {
    button = (
      <Box sx={{ width: { xs: "100%", md: "350px" } }}>
        <Button
          className="ready"
          fullWidth
          buttonType={enrollmentStep ? "secondary" : "primary"}
          onClick={onSubmit}
          sx={{ marginBottom: "10px" }}
        >
          {buttonText}
        </Button>
      </Box>
    );
  }
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      {button}
    </Box>
  );
};

export default Finicity;
